import './template-styles/ArticlePage.scss'

import React, { useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { dynamicGa4DataLayerPush, isProductHub } from '@which/shared'

import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react'

import type { Author, ImageWithSources, ReviewsAdviceSingleQuery } from '../../generated/frontend'
import { useReviewsAdviceSingleQuery } from '../../generated/frontend'
import { useBackStack } from '../../shared/BrowserBackStackProvider'
import {
  AssociatedArticles,
  BackToTop,
  Breadcrumb,
  ErrorComponent,
  Loader,
  PageError,
  ResubscribeBanner,
} from '../../shared/components'
import { AffiliateDisclaimer } from '../../shared/components/AffiliateDisclaimer/AffiliateDisclaimer'
import { HideOnArticleScroll } from '../../shared/components/HideOnScroll/HideOnArticleScroll'
import { LocalFooter } from '../../shared/components/LocalFooter/LocalFooter'
import {
  PromoBlock,
  PromoBlockSticky,
  PromoBlockWithHeroImage,
} from '../../shared/components/PromoBlock/PromoBlock'
import { Redirect } from '../../shared/components/Redirect'
import { SharingOptions } from '../../shared/components/Sharing/SharingOptions'
import { getDataLayerScripts } from '../../shared/data-layer'
import { getDFPScripts } from '../../shared/dfp'
import { GridArticle, GridItemArticle } from '../../shared/grids'
import { useBaseMetaTags } from '../../shared/hooks/useBaseMetaTags'
import { useFullUrl } from '../../shared/hooks/useFullUrl'
import { articleBodyRenderer } from '../../shared/renderers/article-body-renderer'
import type { PageInfo } from '../../shared/types/PageInfo'
import { usePageProps } from '../../shared/usePageProps'
import { getPreviousUrl } from '../../shared/utils/getPreviousUrl/getPreviousUrl'
import { removeFalsy } from '../../shared/utils/remove-falsy'
import { PageWrapperWithMeta } from '../../shared/wrappers/PageWrapperWithMeta'
import { ArticleHero } from '../article/components/ArticleHero/ArticleHero'
import { AuthorWrapper } from '../article/components/Author/AuthorWrapper'
import { Header } from '../article/components/Header/Header'
import { IntroParagraph } from '../article/components/IntroParagraph/IntroParagraph'
import { WhichConnectMessage } from '../article/components/WhichConnectMessage/WhichConnectMessage'
import { ToastNotification } from '../reviews/shared/components/ToastNotification'
import { ArticleTemplateContext } from './ArticleTemplateContext'
import { CompareTrayWrapper } from './components/CompareTrayWrapper/CompareTrayWrapper'
import { ArticlePageBodyGam } from './components/Gam/ArticlePageBodyGam'
import { ArticlePageRhsGam } from './components/Gam/ArticlePageRhsGam'
import { JumpLinks } from './components/JumpLinks/JumpLinks'
import { RecommendedForYouArticles } from './components/RecommendedForYouArticles/RecommendedForYouArticles'
import { RecommendedForYouPageBody } from './components/RecommendedForYouArticles/RecommendedForYouPageBody'
import type { SquirrelScriptsData } from './hooks/use-squirrel-script'
import { useSquirrelScript } from './hooks/use-squirrel-script'
import { UserFeedbackIframe } from './page-snippets/user-feedback-iframe'
import { baseDataLayer } from './utils/base-data-layer'
import { dynamicTableDatalayer } from './utils/dynamic-table-datalayer'
import { renderResubscribeBanner } from './utils/render-resubscribe-banner'
import { useRevealOnPageScroll } from './utils/reveal-on-page-scroll'
import { generateSchema } from './utils/schema'

const ReviewsAdviceSinglePage: React.FC = () => {
  const { pathname, search } = useLocation()
  const { context = '' } = usePageProps()
  const stickyContainer = useRef<HTMLDivElement>(null)
  const showTransition = useRevealOnPageScroll(stickyContainer)

  const { loading, error, data } = useReviewsAdviceSingleQuery({
    variables: { url: `${pathname}${search}`, context },
  })

  const { getFullUrl } = useFullUrl()
  const backStack = useBackStack()
  const previousUrl = getPreviousUrl({ backStack })

  const { getBaseMetaTags } = useBaseMetaTags()

  useSquirrelScript(data as SquirrelScriptsData)

  const resubscribeBannerFlag = useFeatureIsOn('resubscribe-banner')
  const gbTestValue = useFeatureValue('ctx-011-contextualised-newsletter', '')
  const productHubSidebarTest = useFeatureValue('whc-812-hub-sidebar', null)
  const moneyCTATest = useFeatureValue('whc-873-money-cta-changes', 'Control')

  const { userAccessState } = usePageProps()
  const transformTypeDecision = userAccessState?.transformTypeDecision ?? ''
  const isPaidMember = ['AUTHENTICATED_FULL_ACCESS', 'FULL_ACCESS'].includes(transformTypeDecision)

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.articlePage) {
    return <PageError pageName="Article Page" />
  }

  const { articlePage } = data

  if (articlePage.__typename === 'Redirect') {
    return <Redirect to={articlePage.redirectUrl} />
  }

  const {
    articleBodyTemplate,
    articleConfig,
    authors,
    breadcrumb,
    header,
    heroImage,
    introParagraph,
    jumpLinks,
    meta,
    moreOnArticles,
    optionalArticleData,
    relatedArticles,
    sharingData,
    showResubBanner,
  } = articlePage

  const baseMetaTags = getBaseMetaTags({
    meta,
    context,
    imageUrl: heroImage?.src,
    twitterCard: 'summary_large_image',
  })
  const metaTags = getMetaTags({
    articlePage,
    baseMetaTags,
    urls: { currentUrl: getFullUrl(), previousUrl },
    gbTestValue,
  })

  const gamPageInfo = {
    vertical: meta.taxonomyHierarchy.vertical?.slug,
    sub_vertical: meta.taxonomyHierarchy.subVertical?.slug,
    category: meta.taxonomyHierarchy.category?.slug,
    pageType: 'article',
    tags: meta.keywords,
    content_id: meta.contentId,
  }

  const moneyPromoBlock = optionalArticleData?.components?.moneyPromoBlock
  const { title, description, ctaLabel, ctaLink } = moneyPromoBlock ?? {}
  const consumerRightsAndMoneyLocalFooter = optionalArticleData?.components?.localFooter
  const isInsuranceArticle = meta.taxonomyHierarchy?.subVertical?.slug === 'insurance'
  const showPromoBlock = moneyPromoBlock && (!isInsuranceArticle || moneyCTATest === 'Control')
  const showPromoBlockWithHeroImage =
    heroImage && moneyPromoBlock && isInsuranceArticle && moneyCTATest === 'Variation 2'
  const showStickyPromoBlock = moneyPromoBlock && isInsuranceArticle && moneyCTATest !== 'Control'
  const newCtaLabel = ctaLabel?.replace('Get a quote now', 'Get a quote')
  const isConfused = description?.indexOf('by Confused.com') > -1
  const newDescription = isConfused
    ? 'Check Which? insurance ratings and compare deals using the service provided by Confused.com'
    : description
  const newTitle = isConfused ? `${title} with Confused.com` : title
  const sharedTrackingProps = {
    event: 'click_cta',
    utagid: 'WHC873DP01',
    item_group: 'in article cta',
  }
  const promoBlockTracking = (position: string) => {
    return isInsuranceArticle
      ? {
          onClick: () => {
            dynamicGa4DataLayerPush({
              ...sharedTrackingProps,
              item_text: ctaLabel,
              item_parent_text: title,
              item_spec: position,
            })
          },
        }
      : {}
  }

  const handleShowMoreOnJumplinkClick = () => {
    const showMoreBtn = document.querySelector<HTMLButtonElement>(
      '[data-testid="show-more-button"]'
    )

    if (showMoreBtn) {
      showMoreBtn.click()
    }
  }

  // TODO: We might want to do some of this logic in data-provider if implementing properly
  // Added for AB test WHC-812
  let moreOnThisAdditionalProps = {}

  if (meta && meta.taxonomyHierarchy) {
    const { category } = meta.taxonomyHierarchy
    const productHubCategory = isProductHub(category)

    if (productHubCategory) {
      const productHubLink = `/l/${meta.taxonomyHierarchy.subVertical.slug}/${category.slug}`

      if (productHubSidebarTest === 'Variation 1') {
        moreOnThisAdditionalProps = {
          title: '',
          titleCategory: category.name.toLowerCase(),
          titleCategoryLink: productHubLink,
        }
      } else if (productHubSidebarTest === 'Variation 2') {
        moreOnThisAdditionalProps = {
          title: `More on ${category.name.toLowerCase()}`,
          titleCategory: category.name.toLowerCase(),
          titleCategoryLink: productHubLink,
          seeAllLink: true,
        }
      }
    }
  }

  return (
    <ArticleTemplateContext.Provider
      value={{
        meta,
      }}
    >
      <div
        ref={domUpdates}
        data-testid="template-portal"
        data-template-type="reviews-advice-single-article"
      >
        {renderResubscribeBanner(resubscribeBannerFlag, transformTypeDecision) && (
          <ResubscribeBanner showBanner={showResubBanner} />
        )}
        <Breadcrumb {...breadcrumb} />
        <PageWrapperWithMeta metaTags={metaTags}>
          <GridArticle>
            <GridItemArticle area="header">
              <AffiliateDisclaimer affiliateDisclaimer={articleConfig?.affiliateDisclaimer} />
              <Header {...header} />
            </GridItemArticle>
            <GridItemArticle area="author">
              <AuthorWrapper authors={authors} />
            </GridItemArticle>
            <GridItemArticle area="hero">
              <ArticleHero
                heroImage={heroImage as ImageWithSources}
                showPromoBlock={showPromoBlockWithHeroImage}
              />
              {showPromoBlockWithHeroImage && (
                <PromoBlockWithHeroImage
                  title={title}
                  description={newDescription}
                  ctaLabel={newCtaLabel}
                  ctaLink={ctaLink}
                  onClick={() => {
                    dynamicGa4DataLayerPush({
                      ...sharedTrackingProps,
                      item_text: newCtaLabel,
                      item_parent_text: title,
                      item_spec: 'in image',
                    })
                  }}
                />
              )}
            </GridItemArticle>
            <GridItemArticle area="jumplinks">
              <JumpLinks handleOnClick={handleShowMoreOnJumplinkClick} links={jumpLinks} />
            </GridItemArticle>
            <GridItemArticle area="share" className="gridarea-share">
              <HideOnArticleScroll>
                <SharingOptions sharingData={sharingData} />
              </HideOnArticleScroll>
            </GridItemArticle>
            <GridItemArticle area="body" className="gridarea-body">
              <div ref={stickyContainer}>
                {/* This div controls the scroll range of promo sticky block */}
                {showStickyPromoBlock && (
                  <PromoBlockSticky
                    title={newTitle}
                    ctaLabel={newCtaLabel}
                    ctaLink={ctaLink}
                    showTransition={showTransition}
                    onClick={() => {
                      dynamicGa4DataLayerPush({
                        ...sharedTrackingProps,
                        item_text: newCtaLabel,
                        item_parent_text: newTitle,
                        item_spec: 'sticky header',
                      })
                    }}
                  />
                )}
                <IntroParagraph text={introParagraph} />
                {articleBodyRenderer(articleBodyTemplate)}
                <WhichConnectMessage showMessage={articleConfig?.whichConnectMessage} />
                {showPromoBlock && (
                  <PromoBlock {...moneyPromoBlock} {...promoBlockTracking('bottom of page')} />
                )}
              </div>
              <div className="related-footer">
                <AssociatedArticles
                  componentType="more-on-this"
                  position="bottom"
                  className="associated-articles"
                  {...moreOnArticles}
                  {...moreOnThisAdditionalProps}
                />
                <div className="associated-articles">
                  <RecommendedForYouPageBody
                    componentType="related-articles"
                    position="bottom"
                    {...relatedArticles}
                  />
                </div>
              </div>
              <UserFeedbackIframe />
              <ArticlePageBodyGam pageInfo={gamPageInfo} />
              <SharingOptions sharingData={sharingData} orientation="vertical" />
              <div className="back-to-top-wrapper">
                <BackToTop />
              </div>
            </GridItemArticle>
            <GridItemArticle area="related" className="gridarea-related">
              <aside className="sidebar">
                {showPromoBlock && (
                  <PromoBlock {...moneyPromoBlock} {...promoBlockTracking('sidebar')} />
                )}
                <AssociatedArticles
                  componentType="more-on-this"
                  position="right"
                  {...moreOnArticles}
                  {...moreOnThisAdditionalProps}
                />
                <RecommendedForYouArticles />
                <ArticlePageRhsGam pageInfo={gamPageInfo} />
              </aside>
            </GridItemArticle>
            {consumerRightsAndMoneyLocalFooter && (
              <GridItemArticle area="footer">
                <LocalFooter data={consumerRightsAndMoneyLocalFooter} />
              </GridItemArticle>
            )}
          </GridArticle>
        </PageWrapperWithMeta>
      </div>
      {isPaidMember && (
        <CompareTrayWrapper taxonomySlug={meta?.taxonomyHierarchy?.category?.slug} />
      )}
      <ToastNotification />
    </ArticleTemplateContext.Provider>
  )
}

export default ReviewsAdviceSinglePage

const domUpdates = () => {
  dynamicTableDatalayer()
}

export type ReviewsAdviceSingleArticleData = ReviewsAdviceSingleQuery['articlePage'] & {
  __typename: 'ArticlePage'
}

const getMetaTags = ({
  articlePage: { meta, articleBodyTemplate, heroImage, authors, articleConfig },
  baseMetaTags,
  urls,
  gbTestValue,
}: {
  articlePage: ReviewsAdviceSingleArticleData
  baseMetaTags: JSX.Element[]
  urls: { currentUrl: string; previousUrl?: string }
  gbTestValue: string
}) => {
  const authorsMeta = authors?.length
    ? [<meta key="author" name="author" content={authors.map(({ name }) => name).join(', ')} />]
    : []

  const keywords = meta.keywords?.length
    ? [<meta key="keywords" name="keywords" content={meta.keywords.join(', ')} />]
    : []

  const basePageInfo: PageInfo = {
    pageType: 'article',
    vertical: meta.taxonomyHierarchy?.vertical.slug,
    sub_vertical: meta.taxonomyHierarchy?.subVertical?.slug,
    super_category: meta.taxonomyHierarchy?.superCategory?.slug,
    category: meta.taxonomyHierarchy?.category?.slug,
    content_id: meta.contentId,
    isVideoArticle: false,
  }

  // data layer
  const dataLayerPageInfo: PageInfo = {
    ...basePageInfo,
    ...baseDataLayer(meta, urls, articleBodyTemplate),
    keywords: meta.keywords?.length ? meta.keywords.join(' | ') : '',
    tags: meta.keywords?.length ? meta.keywords.join(' | ') : '',
    author: authors?.length ? authors.map(({ name }) => name).join(' | ') : '',
    adviceType: articleConfig.adviceType,
    articleType: articleConfig.articleType,
    content_type: 'advice',
  }
  const dataLayer = getDataLayerScripts([...(meta.dataLayer || []), removeFalsy(dataLayerPageInfo)])

  // doubleclick for publishers
  const dfpMeta = getDFPScripts(meta.dataLayer, {
    ...basePageInfo,
    keywords: meta.keywords,
    tags: meta.keywords,
    GB_test_value: gbTestValue,
  })

  // schema for SEO
  const schema = generateSchema({
    meta,
    authors: (authors || []) as Author[],
    imageList: heroImage?.renditions,
  })

  return baseMetaTags.concat(
    authorsMeta,
    keywords,
    dataLayer,
    dfpMeta,
    schema,
    <link rel="preconnect" href="https://securepubads.g.doubleclick.net" />
  )
}
